import React, { useEffect, useState } from "react";
import { useAuth } from "../../../../contexts/auth-context/AuthContextProvider";
import { formatPhoneNumber } from "../../../../components/CustomPhoneInput/phoneUtils";
import { CustomBottomSheet } from "../../../../components/UI/CustomBottomSheet";
import { useTranslation } from "react-i18next";
import { IonIcon, IonInput, IonSkeletonText, useIonToast } from "@ionic/react";
import { useGetProfileQuery, useUpdateProfileMutation } from "../../../../providers/profile";
import { Controller, useForm } from "react-hook-form";
import styles from "./profile-info.module.scss";
import classNames from "classnames/bind";
import { logoIcon } from "../../../../parhato2/src/shared/assets/icons/icons";
import { useConfig } from "../../../../providers/config";
import useGetReferralPointsQuery from "../../../../providers/referrals/useGetReferralPointsQuery";

const cx = classNames.bind(styles);

type FormValues = {
  firstName: string;
};

interface ProfileInfoProps {
  showPreview?: boolean;
  onNameConfirmed?: (name: string) => void;
  onClose?: () => void;
  forceOpen?: boolean;
}

const ProfileInfo = ({ showPreview = true, onNameConfirmed, onClose, forceOpen = false }: ProfileInfoProps) => {
  const { t } = useTranslation();
  const [presentToast] = useIonToast();
  const { user, updateUserData } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const userId = user?.id;
  const config = useConfig();
  const isGetReferralPointsQueryEnabled = config.isReferralEnabled && !!userId;

  const { data: userRewardPoints = 0, isLoading } = useGetReferralPointsQuery({
    userId: userId!,
    options: {
      enabled: isGetReferralPointsQueryEnabled,
    },
  });

  const { data: fetchedProfile, isLoading: isLoadingProfile } = useGetProfileQuery({
    userId: user?.id,
    options: {
      enabled: !!user?.id,
    },
  });

  const { handleSubmit, control, setValue } = useForm<FormValues>({
    mode: "onSubmit",
    defaultValues: {
      firstName: "",
    },
  });

  useEffect(() => {
    if (isLoadingProfile) return;
    if (fetchedProfile?.firstName) {
      setValue("firstName", fetchedProfile.firstName);
    } else {
      setIsOpen(true);
    }

    // Если forceOpen = true, всегда открываем BS
    if (forceOpen) {
      setIsOpen(true);
    }
  }, [fetchedProfile, isLoadingProfile, setValue, forceOpen]);

  const { mutate: updateProfile, isLoading: isUpdating } = useUpdateProfileMutation();
  const parsedPhone = user?.phone ? formatPhoneNumber(user.phone) : "";

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const updateInfo = async (data: FormValues) => {
    const firstName = data?.firstName;

    updateProfile(
      {
        userId: user?.id!,
        userPayload: { firstName, phone: fetchedProfile?.phone },
      },
      {
        onError: () => {
          presentToast({
            message: t("Error Updating Data!"),
            color: "danger",
            duration: 2000,
          });
        },
        onSuccess: () => {
          handleClose();
          updateUserData({ ...user, firstName });
          presentToast({
            message: t("Data successfully updated"),
            color: "success",
            duration: 2000,
          });

          // Если это вызов из корзины, то вызываем callback
          onNameConfirmed?.(firstName);
        },
      },
    );
  };

  if (showPreview && isLoadingProfile)
    return <IonSkeletonText className="w-full h-[4.5rem] lg:h-[11rem] rounded-xl m-0" animated />;

  return (
    <CustomBottomSheet
      open={isOpen}
      onDismiss={handleClose}
      showDefaultHeader={false}
      showDefaultPreview={showPreview}
      contentClassName={cx("bottom_sheet", "flex flex-col items-center")}
      snapPoints={({ minHeight }) => [minHeight + 100, minHeight + 100]}
      renderPreview={() =>
        showPreview && (
          <div
            className="flex justify-between rounded-xl h-full lg:h-[11rem] p-3 text-gray-900 bg-[#f4f4f4] cursor-pointer"
            onClick={handleOpen}
            role="button"
            tabIndex={0}
          >
            <div className="flex flex-col ">
              <span className="font-bold ">{fetchedProfile?.firstName ? fetchedProfile?.firstName : "Имя"} </span>
              <span className="text-[#888] text-[1.0625rem] lg:text-[1.1rem]">{parsedPhone}</span>
            </div>
            <div className="flex flex-col ">
              <span className="font-bolt">Ваши баллы:</span>
              {isLoading ? (
                <IonSkeletonText className="h-4 w-20" />
              ) : (
                <div className="flex items-center justify-end gap-1">
                  <div className="font-bold">{userRewardPoints}</div>
                  <IonIcon icon={logoIcon} className="bg-primary p-1 text-[14px] rounded-lg" />
                </div>
              )}
            </div>
          </div>
        )
      }
      footer={
        <button
          disabled={isUpdating}
          onClick={() => handleSubmit(updateInfo)()}
          className="w-full font-bold py-4 bg-primary disabled:opacity-50"
        >
          {isUpdating ? t("Saving...") : t("Save")}
        </button>
      }
    >
      <form onSubmit={handleSubmit(updateInfo)} className="w-full h-full flex flex-col items-center px-4 pt-24">
        <div>Имя</div>
        <Controller
          name="firstName"
          control={control}
          rules={{ required: "Имя обязательно", minLength: { value: 3, message: "Имя должно быть минимум 3 символа" } }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <IonInput
                autofocus
                type="text"
                value={value}
                onIonChange={(e) => onChange(e.detail.value)}
                className={cx("input", "border font-bold text-[1.875rem] text-center rounded-md w-full")}
              />
              {error && <div className="text-sm text-red-500 text-center mt-1">{error.message}</div>}
            </>
          )}
        />
        <div className="flex flex-col items-center gap-1">
          <div className="text-sm text-gray-400 text-center">{t("In Russian or Latin letters")}</div>
          <div className="text-sm text-gray-400 text-center">{t("You can use a hyphen")}</div>
        </div>
      </form>
    </CustomBottomSheet>
  );
};

export default ProfileInfo;
